import axios from '@/plugins/axios.js';

const initialState = {
  numberOfPayments: 0,
  numberOfFullPayments: 0,
  fullPaymentAmount: 0,
  lastPaymentAmount: 0,
  paymentFrequency: '',
  firstPaymentDate: '',
  lastPaymentDate: '',
  isEOM: false,
  paymentSchedule: [],
  disclosuresSent: false,
  paymentSchedulesSent: false,
  isSettlementAvailable: false,
  isManualPayment: false,
  minSettlementAmount: 0,
  maxSettlementAmount: 0,
  reducedPayoffPlanOptions: [],
  latestSingleSettlement: null,
  latestReducedPayoffPlan: null,
  pdsPaymentSchedule: [],
};

const actions = {
  setIsManualPayment({ commit }, manualPayment) {
    commit('SET_MANUAL_PAYMENT', manualPayment);
  },

  // eslint-disable-next-line no-unused-vars
  async initializeSettlement({ commit }, { ticketId, lpLoan }) {
    const res = await axios
      .get(`api/v1/support/ticket/${ticketId}/pds-settlement/${lpLoan}/initialize-settlement`)
      .catch(this.errorHandler);

    commit('SET_IS_SETTLEMENT_AVAILABLE', res.data.isEligibleForSettlement);
    commit('SET_MIN_SETTLEMENT_AMOUNT', res.data.minSettlementAmount);
    commit('SET_MAX_SETTLEMENT_AMOUNT', res.data.maxSettlementAmount);
    commit('SET_REDUCED_PAYOFF_PLAN_OPTIONS', res.data.reducedPayOffPlanOptions);
    commit('SET_FIRST_PAYMENT_DATE', res.data.firstPaymentDateInPlan);
    commit('SET_LATEST_SINGLE_SETTLEMENT', res.data.latestSingleSettlement);
    commit('SET_LATEST_REDUCED_PAYOFF_PLAN', res.data.latestReducedPayoffPlan);
  },

  // eslint-disable-next-line no-unused-vars
  async createSettlementSinglePaymentPlan({ commit }, { ticketId, lpLoan, formData }) {
    return axios
      .post(`api/v1/support/ticket/${ticketId}/pds-settlement/${lpLoan}/create-single-payment-plan`, formData)
      .catch(this.errorHandler);
  },

  // eslint-disable-next-line no-unused-vars
  async createSettlementReducedPayoffPlan({ commit }, { ticketId, lpLoan, formData }) {
    return axios
      .post(`api/v1/support/ticket/${ticketId}/pds-settlement/${lpLoan}/create-reduced-payoff-plan`, formData)
      .catch(this.errorHandler);
  },

  async getPdsSettlementSchedules(_, { ticketId, lpLoan, formData }) {
    return axios
      .post(`api/v1/support/ticket/${ticketId}/pds-settlement/${lpLoan}/get-settlement-schedules`, formData)
      .catch(this.errorHandler);
  },

  async updatePdsPaymentSchedule({ commit }, data) {
    commit('SET_PDS_PAYMENT_SCHEDULE', data);
  },

  // eslint-disable-next-line no-unused-vars
  async deleteSettlementPlan({ commit }, { settlementDeleteEndpoint }) {
    return axios
      .delete(settlementDeleteEndpoint)
      .catch(this.errorHandler);
  },

  // eslint-disable-next-line no-unused-vars
  async getPeriodPaymentAmountForSettlementTerms({ commit }, { ticketId, lpLoan, data }) {
    return axios
      .post(`api/v1/support/ticket/${ticketId}/pds-settlement/${lpLoan}/periodic-payment-by-settlement-terms`, data)
      .catch(this.errorHandler);
  },

  determineAvailability({ commit }, { loanStatusId, loanSubStatusId }) {
    // allowed loan status and sub-status for settlement
    const allowedLoanStatus = {
      2: [
        9, 147,
      ],
      17: [
        149, 150, 151, 152, 217,
      ],
      19: [
        159,
      ],
      20: [
        177,
      ],
    };

    commit(
      'SET_IS_SETTLEMENT_AVAILABLE',
      loanStatusId in allowedLoanStatus
        && allowedLoanStatus[loanStatusId].includes(loanSubStatusId),
    );
  },

  async getPaymentSchedule({ commit }, { offer }) {
    const frequency = offer.isSinglePayment ? 'single'
      : offer.paymentFrequency.replace('-', '').toLowerCase();

    const res = await axios
      .post('api/v1/admin/payment-schedule', {
        firstPaymentDate: offer.firstPaymentDate,
        totalOfPayments: offer.settlementAmount,
        paymentAmount: offer.paymentAmount,
        paymentFrequency: frequency,
      });

    // console.log(res.data);
    commit('SET_NUMBER_OF_PAYMENTS', res.data.numberOfPayments);
    commit('SET_NUMBER_OF_FULL_PAYMENTS', res.data.numberOfFullPayments);
    commit('SET_FULL_PAYMENT_AMOUNT', res.data.fullPaymentAmount);
    commit('SET_LAST_PAYMENT_AMOUNT', res.data.lastPaymentAmount);
    commit('SET_PAYMENT_FREQUENCY', offer.paymentFrequency.toLowerCase());
    commit('SET_PAYMENT_SCHEDULE', [...res.data.schedule]);
    commit('SET_FIRST_PAYMENT_DATE', res.data.schedule[0].date);
    commit('SET_LAST_PAYMENT_DATE', res.data.schedule.at(-1).date);
    commit('SET_IS_EOM', res.data.isEOM);
  },

  async sendCommunication({ commit }, { ticketId, message, action }) {
    // MAKE LOANPRO CALL
    const response = await axios
      .post('api/v1/support/ticket/settlement/send-communication', {
        ticketId,
        message,
      })
      .catch(this.errorHandler);

    commit(action, (response.status === 200));
  },
  paymentSchedulesNotSent({ commit }) {
    commit('SET_PAYMENT_SCHEDULES_SENT', false);
  },
  disclosuresNotSent({ commit }) {
    commit('SET_DISCLOSURES_SENT', false);
  },
  // eslint-disable-next-line no-unused-vars
  async checkPaymentDate({ commit }, { paymentAccountId, paymentDate, singlePayment = false }) {
    return axios.get(
      `api/v1/support/ticket/loan/autopays/account/${paymentAccountId}/check-payment-date?date=${paymentDate}&singlePayment=${singlePayment}`,
    );
  },

  async checkManualPaymentDate(_, { paymentDate }) {
    return axios.get(
      `api/v1/support/ticket/loan/manual-payment/check-payment-date?date=${paymentDate}`,
    );
  },
};

const mutations = {
  SET_MANUAL_PAYMENT(state, data) {
    state.isManualPayment = data;
  },
  SET_NUMBER_OF_PAYMENTS(state, data) {
    state.numberOfPayments = data;
  },
  SET_NUMBER_OF_FULL_PAYMENTS(state, data) {
    state.numberOfFullPayments = data;
  },
  SET_FULL_PAYMENT_AMOUNT(state, data) {
    state.fullPaymentAmount = data;
  },
  SET_LAST_PAYMENT_AMOUNT(state, data) {
    state.lastPaymentAmount = data;
  },
  SET_PAYMENT_FREQUENCY(state, data) {
    state.paymentFrequency = data;
  },
  SET_PAYMENT_SCHEDULE(state, data) {
    state.paymentSchedule = data;
  },
  SET_FIRST_PAYMENT_DATE(state, data) {
    state.firstPaymentDate = data;
  },
  SET_LAST_PAYMENT_DATE(state, data) {
    state.lastPaymentDate = data;
  },
  SET_IS_EOM(state, data) {
    state.isEOM = data;
  },
  SET_DISCLOSURES_SENT(state, data) {
    state.disclosuresSent = data;
  },
  SET_PAYMENT_SCHEDULES_SENT(state, data) {
    state.paymentSchedulesSent = data;
  },
  SET_IS_SETTLEMENT_AVAILABLE(state, data) {
    state.isSettlementAvailable = data;
  },
  SET_MIN_SETTLEMENT_AMOUNT(state, data) {
    state.minSettlementAmount = data;
  },
  SET_MAX_SETTLEMENT_AMOUNT(state, data) {
    state.maxSettlementAmount = data;
  },
  SET_REDUCED_PAYOFF_PLAN_OPTIONS(state, data) {
    state.reducedPayoffPlanOptions = data;
  },
  SET_LATEST_SINGLE_SETTLEMENT(state, data) {
    state.latestSingleSettlement = data;
  },
  SET_LATEST_REDUCED_PAYOFF_PLAN(state, data) {
    state.latestReducedPayoffPlan = data;
  },
  SET_PDS_PAYMENT_SCHEDULE(state, data) {
    state.pdsPaymentSchedule = data;
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
};
